import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Wallet, Search, ArrowUpCircle, ArrowDownCircle, RefreshCw } from 'lucide-react';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import { walletAPI, clientesAPI } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

export default function AdminWallet() {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [wallets, setWallets] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [extrato, setExtrato] = useState([]);
  const [modal, setModal] = useState(null);
  const [valor, setValor] = useState('');
  const [descricao, setDescricao] = useState('');

  const card = `rounded-xl p-5 ${darkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white border border-gray-200'}`;
  const textP = darkMode ? 'text-white' : 'text-gray-900';
  const textS = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textT = darkMode ? 'text-gray-500' : 'text-gray-400';
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const cliRes = await clientesAPI.list();
      const cls = cliRes.data || [];
      setClientes(cls);
      const saldos = {};
      for (const c of cls) {
        try { const r = await walletAPI.getSaldo(c.id); saldos[c.id] = Number(r.data?.saldo || 0); }
        catch { saldos[c.id] = 0; }
      }
      setWallets(saldos);
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  };

  const selectCliente = async (c) => {
    setSelected(c);
    try {
      const res = await walletAPI.getExtrato(c.id, 50);
      setExtrato(res.data?.data || res.data || []);
    } catch { setExtrato([]); }
  };

  const refreshExtrato = async () => {
    if (!selected) return;
    const [sRes, eRes] = await Promise.all([
      walletAPI.getSaldo(selected.id),
      walletAPI.getExtrato(selected.id, 50),
    ]);
    setWallets(prev => ({ ...prev, [selected.id]: Number(sRes.data?.saldo || 0) }));
    setExtrato(eRes.data?.data || eRes.data || []);
  };

  const handleTransacao = async () => {
    const v = parseFloat(valor);
    if (!v || v <= 0) return toast.error('Valor inválido');
    if (!descricao.trim()) return toast.error('Descrição obrigatória');
    const labels = { creditar: 'creditado', debitar: 'debitado', estorno: 'estornado' };
    try {
      await walletAPI[modal.tipo]({ cliente_id: selected.id, valor: v, descricao: descricao || undefined, criado_por: user?.id });
      toast.success(`R$ ${v.toFixed(2)} ${labels[modal.tipo]}`);
      setModal(null); setValor(''); setDescricao('');
      refreshExtrato();
    } catch (e) { toast.error(e.response?.data?.message || 'Erro na operação'); }
  };

  const filtrados = clientes.filter(c =>
    (c.nome || c.razao_social || '').toLowerCase().includes(search.toLowerCase())
  );

  const refTipoLabel = { recarga: 'Recarga', campanha: 'Campanha', ajuste: 'Ajuste', estorno: 'Estorno' };

  if (loading) return (
    <Layout><div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-nomoz-orange border-t-transparent" />
    </div></Layout>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader title="Wallet - Créditos" subtitle="Gerencie o saldo de créditos dos clientes" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LISTA CLIENTES */}
          <div className="lg:col-span-1 space-y-4">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${textT}`} />
              <input type="text" placeholder="Buscar cliente..." value={search} onChange={e => setSearch(e.target.value)}
                className={`${inputCls} pl-9`} />
            </div>
            <div className={`${card} p-0 overflow-hidden`}>
              <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
                {filtrados.map(c => {
                  const saldo = wallets[c.id] || 0;
                  const isSelected = selected?.id === c.id;
                  return (
                    <div key={c.id} onClick={() => selectCliente(c)}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer border-b transition-colors ${
                        isSelected
                          ? darkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-100'
                          : darkMode ? 'border-zinc-700 hover:bg-zinc-700/50' : 'border-gray-100 hover:bg-gray-50'
                      }`}>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${textP}`}>{c.nome || c.razao_social}</p>
                        <p className={`text-xs ${textT}`}>{c.email || ''}</p>
                      </div>
                      <span className={`text-sm font-semibold shrink-0 ml-2 ${saldo > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        R$ {saldo.toFixed(2)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* DETALHE CLIENTE */}
          <div className="lg:col-span-2 space-y-4">
            {!selected ? (
              <div className={`${card} text-center py-20`}>
                <Wallet className={`h-10 w-10 mx-auto mb-3 ${textT}`} />
                <p className={`text-sm ${textS}`}>Selecione um cliente para ver detalhes</p>
              </div>
            ) : (
              <>
                {/* Saldo + ações */}
                <div className={card}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className={`text-sm ${textT}`}>{selected.nome || selected.razao_social}</p>
                      <p className={`text-3xl font-bold ${(wallets[selected.id] || 0) > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        R$ {(wallets[selected.id] || 0).toFixed(2)}
                      </p>
                    </div>
                    <button onClick={refreshExtrato} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
                      <RefreshCw className={`h-4 w-4 ${textT}`} />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    {[
                      { tipo: 'creditar', label: 'Creditar', cls: 'bg-green-600 hover:bg-green-700' },
                      { tipo: 'debitar', label: 'Debitar', cls: 'bg-orange-600 hover:bg-orange-700' },
                      { tipo: 'estorno', label: 'Estorno', cls: 'bg-blue-600 hover:bg-blue-700' },
                    ].map(op => (
                      <button key={op.tipo} onClick={() => setModal({ tipo: op.tipo })}
                        className={`px-4 py-2 rounded-xl text-xs font-medium text-white transition-colors ${op.cls}`}>
                        {op.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Extrato */}
                <div className={card}>
                  <h3 className={`text-sm font-semibold mb-4 ${textP}`}>Extrato ({extrato.length})</h3>
                  {extrato.length === 0 ? (
                    <p className={`text-sm text-center py-8 ${textT}`}>Nenhuma movimentação</p>
                  ) : (
                    <div className="space-y-0">
                      {extrato.map(t => (
                        <div key={t.id} className={`flex items-center justify-between py-3 border-b last:border-0 ${darkMode ? 'border-zinc-700' : 'border-gray-100'}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            {t.tipo === 'credito'
                              ? <ArrowUpCircle className="h-4 w-4 text-green-500 shrink-0" />
                              : <ArrowDownCircle className="h-4 w-4 text-red-500 shrink-0" />
                            }
                            <div className="min-w-0">
                              <p className={`text-sm truncate ${textP}`}>{t.descricao}</p>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  t.referencia_tipo === 'recarga' ? 'bg-green-500/10 text-green-500' :
                                  t.referencia_tipo === 'campanha' ? 'bg-orange-500/10 text-orange-500' :
                                  t.referencia_tipo === 'estorno' ? 'bg-blue-500/10 text-blue-500' :
                                  'bg-gray-500/10 text-gray-400'
                                }`}>{refTipoLabel[t.referencia_tipo] || t.referencia_tipo}</span>
                                <span className={`text-xs ${textT}`}>
                                  {new Date(t.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right shrink-0 ml-3">
                            <p className={`text-sm font-semibold ${t.tipo === 'credito' ? 'text-green-500' : 'text-red-500'}`}>
                              {t.tipo === 'credito' ? '+' : '-'} R$ {parseFloat(t.valor).toFixed(2)}
                            </p>
                            <p className={`text-xs ${textT}`}>Saldo: R$ {parseFloat(t.saldo_posterior).toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {modal && (() => {
        const cfg = {
          creditar: { title: 'Adicionar créditos', btn: 'Creditar', btnCls: 'bg-green-600 hover:bg-green-700', ph: 'Recarga de créditos' },
          debitar: { title: 'Debitar valor', btn: 'Debitar', btnCls: 'bg-orange-600 hover:bg-orange-700', ph: 'Débito manual' },
          estorno: { title: 'Estornar valor', btn: 'Estornar', btnCls: 'bg-blue-600 hover:bg-blue-700', ph: 'Estorno' },
        }[modal.tipo];
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setModal(null)}>
            <div className={`w-full max-w-sm rounded-xl p-6 ${darkMode ? 'bg-zinc-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
              <h3 className={`text-lg font-bold mb-1 ${textP}`}>{cfg.title}</h3>
              <p className={`text-sm mb-5 ${textS}`}>{selected.nome || selected.razao_social} — Saldo: R$ {(wallets[selected.id] || 0).toFixed(2)}</p>
              <div className="space-y-4">
                <div>
                  <label className={`text-xs font-medium ${textS}`}>Valor (R$)</label>
                  <div className="relative mt-1">
                    <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium ${textS}`}>R$</span>
                    <input type="text" inputMode="decimal" value={valor}
                      onChange={e => { const v = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.'); setValor(v); }}
                      placeholder="0,00" className={`${inputCls} pl-10 text-lg font-semibold`} autoFocus />
                  </div>
                </div>
                <div>
                  <label className={`text-xs font-medium ${textS}`}>Descrição *</label>
                  <input type="text" value={descricao} onChange={e => setDescricao(e.target.value)}
                    placeholder={cfg.ph} className={`${inputCls} mt-1`} />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setModal(null)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                  Cancelar
                </button>
                <button onClick={handleTransacao}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition-colors ${cfg.btnCls}`}>
                  {cfg.btn}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </Layout>
  );
}
